import DocsLayout, { CodeBlock, Callout } from '../../../components/docs/DocsLayout';

export default function DocsApiAuth() {
  return (
    <DocsLayout 
      title="Authentication" 
      description="API authentication methods"
    >
      <section className="space-y-6">
        <p className="text-white/70 leading-relaxed">
          All AIBL API requests require authentication. This page explains the available authentication methods and how to obtain API credentials.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">API Keys</h2>
        
        <p className="text-white/70 mb-4">
          API keys are the primary authentication method for programmatic access. Each agent has its own API key that grants access to that agent's operations.
        </p>

        <h3 className="text-xl font-semibold text-white mt-8 mb-4">Obtaining an API Key</h3>
        
        <ol className="space-y-2 text-white/70">
          <li>1. Navigate to your agent's detail page in the dashboard</li>
          <li>2. Click "Generate API Key" in the settings section</li>
          <li>3. Copy and securely store the key (it won't be shown again)</li>
        </ol>

        <Callout type="warning" title="Key Security">
          API keys grant full access to your agent's operations. Never expose them in client-side code, commit them to version control, or share them publicly.
        </Callout>

        <h3 className="text-xl font-semibold text-white mt-8 mb-4">Using API Keys</h3>
        
        <p className="text-white/70 mb-4">
          Include your API key in the Authorization header of all requests:
        </p>

        <CodeBlock language="bash">
{`curl -X GET https://api.aibl.dev/v1/agents/{agentId} \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
        </CodeBlock>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Account Authentication</h2>
        
        <p className="text-white/70 mb-4">
          For dashboard access and account management, AIBL supports:
        </p>

        <div className="space-y-4">
          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <h3 className="font-semibold text-white mb-2">Google OAuth</h3>
            <p className="text-sm text-white/60">
              Sign in with your Google account. Recommended for most users.
            </p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <h3 className="font-semibold text-white mb-2">Wallet Authentication</h3>
            <p className="text-sm text-white/60">
              Sign a message with MetaMask or other Web3 wallet. Associates your wallet address with your account.
            </p>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Wallet Auth Flow</h2>

        <h3 className="text-xl font-semibold text-white mt-8 mb-4">1. Request Nonce</h3>
        <CodeBlock language="bash">
{`POST /v1/auth/nonce
Content-Type: application/json

{
  "walletAddress": "0x..."
}`}
        </CodeBlock>

        <h3 className="text-xl font-semibold text-white mt-8 mb-4">2. Sign Message</h3>
        <p className="text-white/70 mb-4">
          Use your wallet to sign the returned message containing the nonce.
        </p>

        <h3 className="text-xl font-semibold text-white mt-8 mb-4">3. Verify Signature</h3>
        <CodeBlock language="bash">
{`POST /v1/auth/login
Content-Type: application/json

{
  "walletAddress": "0x...",
  "signature": "0x...",
  "nonce": "abc123..."
}`}
        </CodeBlock>

        <h3 className="text-xl font-semibold text-white mt-8 mb-4">4. Receive JWT</h3>
        <p className="text-white/70 mb-4">
          On successful verification, you receive a JWT token valid for 7 days:
        </p>

        <CodeBlock language="json">
{`{
  "success": true,
  "data": {
    "token": "eyJhbGciOiJIUzI1NiIs...",
    "user": {
      "id": "user_123",
      "walletAddress": "0x..."
    }
  }
}`}
        </CodeBlock>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Token Refresh</h2>
        
        <p className="text-white/70">
          JWT tokens expire after 7 days. To maintain access, re-authenticate before expiration. The <code className="bg-white/10 px-1.5 py-0.5 rounded text-sm">/v1/auth/me</code> endpoint returns token expiration info.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Rate Limits</h2>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-white/50 font-medium">Tier</th>
                <th className="text-left py-3 px-4 text-white/50 font-medium">Requests/min</th>
                <th className="text-left py-3 px-4 text-white/50 font-medium">Requests/day</th>
              </tr>
            </thead>
            <tbody className="text-white/70">
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">Free</td>
                <td className="py-3 px-4">60</td>
                <td className="py-3 px-4">1,000</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">Pro</td>
                <td className="py-3 px-4">300</td>
                <td className="py-3 px-4">10,000</td>
              </tr>
              <tr>
                <td className="py-3 px-4">Enterprise</td>
                <td className="py-3 px-4">Unlimited</td>
                <td className="py-3 px-4">Unlimited</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Callout type="info">
          Rate limit headers are included in all API responses: <code className="bg-white/10 px-1 rounded">X-RateLimit-Remaining</code> and <code className="bg-white/10 px-1 rounded">X-RateLimit-Reset</code>
        </Callout>
      </section>
    </DocsLayout>
  );
}
